import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const LiveStatsSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const VERIFIED_PLAYERS = 3635;
  const TRANSFER_INQUIRIES = 7743;

  const stats = [
    {
      icon: 'Users',
      value: VERIFIED_PLAYERS?.toString(),
      prefix: '',
      suffix: '',
      label: t('liveStats.verifiedPlayers'),
      description: t('liveStats.activeOnPlatform'),
      gradient: 'from-blue-600 to-blue-700',
      isReal: true
    },
    {
      icon: 'TrendingUp',
      value: TRANSFER_INQUIRIES?.toString(),
      prefix: '',
      suffix: '',
      label: t('liveStats.transferInquiries'),
      description: t('liveStats.completedConnections'),
      gradient: 'from-emerald-600 to-emerald-700',
      isReal: true
    },
    {
      icon: 'Shield',
      value: '100',
      prefix: '',
      suffix: '%',
      label: t('liveStats.authenticData'),
      description: t('liveStats.verifiedInfoOnly'),
      gradient: 'from-purple-600 to-purple-700',
      isReal: true
    },
    {
      icon: 'CheckCircle',
      value: '24',
      prefix: '',
      suffix: '/7',
      label: t('liveStats.realTimeUpdates'),
      description: t('liveStats.livePlatformMonitoring'),
      gradient: 'from-amber-600 to-amber-700',
      isReal: true
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0,0,0,0.15) 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-100 to-blue-100 rounded-full mb-6 shadow-sm">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <Icon name="Activity" size={18} className="text-emerald-600" />
            <span className="text-sm font-bold text-slate-800">{t('liveStats.authenticPlatformData')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
            <span className="bg-gradient-to-r from-emerald-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent">
              {t('liveStats.realTimeVerified')}
            </span>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('liveStats.allDataAuthentic')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12 md:mb-16">
          {stats?.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden">
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat?.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className={`relative w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br ${stat?.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                <Icon name={stat?.icon} size={28} color="white" />
              </div>
              
              {/* Value */}
              <div className="relative text-4xl md:text-5xl font-black text-slate-900 mb-3">
                {stat?.prefix}{stat?.value}{stat?.suffix}
              </div>
              
              {/* Label */}
              <div className="relative text-base md:text-lg font-bold text-slate-900 mb-2">{stat?.label}</div>
              
              {/* Description */}
              <div className="relative text-sm text-slate-600 mb-4">{stat?.description}</div>
              
              {/* Verified Badge */}
              {stat?.isReal && (
                <div className="relative flex items-center gap-2 text-xs font-bold text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                  <Icon name="CheckCircle" size={14} className="text-emerald-600" />
                  <span>{t('liveStats.verifiedData')}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Authenticity Guarantee Banner */}
        <div className="relative bg-gradient-to-br from-emerald-600 via-blue-600 to-emerald-700 rounded-3xl p-8 md:p-12 overflow-hidden shadow-2xl">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          
          {/* Content */}
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 shadow-xl">
                <Icon name="Shield" size={32} color="white" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3">{t('liveStats.authenticityGuarantee')}</h3>
                <p className="text-base md:text-lg text-white/90 max-w-2xl leading-relaxed">
                  {t('liveStats.authenticityDesc')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/player-profiles')}
              className="flex-shrink-0 px-8 py-4 bg-white text-emerald-600 rounded-xl hover:bg-blue-50 transition-all duration-300 flex items-center gap-3 shadow-xl hover:scale-105 font-bold text-base">
              <span>{t('liveStats.viewVerifiedPlayers')}</span>
              <Icon name="ArrowRight" size={20} className="text-emerald-600" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveStatsSection;